import { Button, CircularProgress, Tooltip } from '@material-ui/core';
import { IEquipmentGrid } from 'interfaces/Equipment';
import React from 'react';
import {
  useExpanded,
  useTable,
  useBlockLayout,
  useResizeColumns,
} from 'react-table';
import AddMoreCell from './components/AddMoreCell';
import CheckboxCell from './components/CheckboxCell';
import LoadMoreCell from './components/LoadMoreCell';
import useStyles from './style';
import { ITable, ITableRef } from './types';

const TableLayout = ({
  getTableProps,
  getTableBodyProps,
  headerGroups,
  rows,
  prepareRow,
  isRowLoading,
  isRowSelected,
  extraRowInfo,
  state: { expanded },
  children,
  rootCode,
  hasMore,
  isLoading,
  handleLoadMore,
  handleLoadChild,
}) => {
  const classes = useStyles();

  const getRowLength = React.useCallback(
    row => {
      let sum = 0;

      if (Array.isArray(row.subRows) && expanded[row.id]) {
        sum += row.subRows.length;
        sum += row.subRows.reduce(
          (acc, subRow) => acc + getRowLength(subRow),
          0,
        );
      }

      return sum;
    },
    [expanded],
  );

  const renderConditionalHeaderToolTip = React.useCallback(
    column => {
      if (column?.tipText) {
        return (
          <Tooltip
            classes={{ tooltip: classes.tooltipCenterContent }}
            title={
              <span className={classes.tooltipLabel}>{column.tipText}</span>
            }
          >
            <span>{column.render('Header')}</span>
          </Tooltip>
        );
      }

      return column.render('Header');
    },
    [classes.tooltipCenterContent, classes.tooltipLabel],
  );

  // TODO: corrigir tipagem do infoFlag
  const rowInfoFlags = React.useMemo(() => {
    const infoFlags = {} as {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [key: string]: { hasMore: boolean; parentRow: any };
    };

    rows.forEach((row, index) => {
      if (extraRowInfo[row.original.code]) {
        const rowLength = getRowLength(row);

        if (rowLength) {
          const infoFlagKey = rowLength + index;
          infoFlags[infoFlagKey] = {
            hasMore: row.subRows.length < extraRowInfo[row.original.code].count,
            parentRow: row,
          };
        }
      }
    });

    if (hasMore) {
      const infoFlagKey = rows.length ? rows.length - 1 : 0;

      infoFlags[infoFlagKey] = {
        hasMore,
        parentRow: null,
      };
    }

    return infoFlags;
  }, [extraRowInfo, getRowLength, hasMore, rows]);

  const getStyles = (props, align) => [
    props,
    {
      style: align
        ? {
            display: 'flex',
            alignItems: 'center',
            justifyContent: {
              center: 'center',
              left: 'flex-start',
              right: 'flex-end',
            }[align],
          }
        : { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    },
  ];

  const isRowHighlighted = React.useCallback(
    ({ id, subRows = [] }: { id: string; subRows: unknown[] }) => {
      const path = id.split('.');

      const isRootRow = path.length === 1;

      if (isRootRow || (expanded[id] && subRows.length)) {
        return '';
      }

      return `${classes.highlightedRow}`;
    },
    [classes.highlightedRow, expanded],
  );

  const headerProps = (props, { column }) => getStyles(props, column.align);

  const cellProps = (props, { cell }) => getStyles(props, cell.column.align);

  return (
    <div className={classes.tableContainer}>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th
                  {...column.getHeaderProps(headerProps)}
                  className={column.canResize ? classes.resizableColumn : ''}
                >
                  {renderConditionalHeaderToolTip(column)}
                  {column.canResize && (
                    <div
                      {...column.getResizerProps()}
                      className={classes.resizableColumn}
                    />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);

            const jsxRows: JSX.Element[] = [];

            jsxRows.push(
              <tr {...row.getRowProps()} className={isRowHighlighted(row)}>
                {row.cells.map(cell => {
                  return (
                    <td {...cell.getCellProps(cellProps)}>
                      {cell.render('Cell', {
                        isLoading: isRowLoading[row.id],
                        isExpanded: expanded[row.id],
                        isChecked: isRowSelected[row.original.id],
                      })}
                    </td>
                  );
                })}
              </tr>,
            );

            const rowInfo = rowInfoFlags[rowIndex];

            if (rowInfo?.hasMore) {
              if (rowInfo?.parentRow) {
                if (expanded[rowInfo.parentRow.id]) {
                  jsxRows.push(
                    <tr {...row.getRowProps()} key={rowIndex}>
                      <td colSpan={row.cells.length} style={{ width: '100%' }}>
                        <Button
                          color="primary"
                          disabled={Boolean(
                            isRowLoading[rowInfo.parentRow?.id],
                          )}
                          style={{ width: '100%' }}
                          onClick={() => {
                            handleLoadChild({
                              ...rowInfo.parentRow,
                              isExpanded: expanded[rowInfo.parentRow.id],
                            });
                          }}
                        >
                          {`Carregar mais itens para a instalação ${rowInfo.parentRow.original.code}`}

                          {isRowLoading[rowInfo.parentRow?.id] && (
                            <CircularProgress
                              style={{ marginLeft: '8px' }}
                              aria-label="row-loading"
                              color="inherit"
                              size={25}
                            />
                          )}
                        </Button>
                      </td>
                    </tr>,
                  );
                }
              } else {
                jsxRows.push(
                  <tr {...row.getRowProps()} key={rowIndex}>
                    <td colSpan={row.cells.length} style={{ width: '100%' }}>
                      <Button
                        color="primary"
                        disabled={Boolean(isLoading)}
                        style={{ width: '100%' }}
                        onClick={() => {
                          handleLoadMore();
                        }}
                      >
                        {`Carregar mais itens para a instalação ${rootCode}`}

                        {isLoading && (
                          <CircularProgress
                            style={{ marginLeft: '8px' }}
                            aria-label="row-loading"
                            color="inherit"
                            size={25}
                          />
                        )}
                      </Button>
                    </td>
                  </tr>,
                );
              }
            }

            return jsxRows;
          })}
        </tbody>
      </table>
      {children}
    </div>
  );
};

const TableTree = React.forwardRef<ITableRef, ITable<IEquipmentGrid>>(
  (props, ref) => {
    const {
      rootCode,
      hasMore,
      isLoading,
      data,
      columns,
      isRowLoading,
      isRowSelected,
      extraRowInfo,
      NoDataComponent,
      LoadingDataComponent,
      handleLoadMore,
      handleLoadChild,
    } = props;

    const defaultColumn = React.useMemo(
      () => ({
        minWidth: 40,
        width: 150,
        maxWidth: 1200,
      }),
      [],
    );

    const tableInstance = useTable(
      {
        columns,
        data,
        defaultColumn,
        autoResetExpanded: false,
        autoResetResize: false,
      },
      useExpanded,
      useBlockLayout,
      useResizeColumns,
    );

    React.useImperativeHandle(ref, () => ({
      resetExpandedRow: () => {
        tableInstance.toggleAllRowsExpanded(false);
      },
    }));

    const ChildComponent = React.useCallback(() => {
      if (isLoading) {
        return <>{LoadingDataComponent}</>;
      }

      if (!data.length) {
        return <>{NoDataComponent}</>;
      }

      return null;
    }, [LoadingDataComponent, NoDataComponent, data.length, isLoading]);

    return (
      <TableLayout
        {...tableInstance}
        rootCode={rootCode}
        isLoading={isLoading}
        hasMore={hasMore}
        handleLoadMore={handleLoadMore}
        isRowLoading={isRowLoading}
        isRowSelected={isRowSelected}
        extraRowInfo={extraRowInfo}
        handleLoadChild={handleLoadChild}
      >
        {ChildComponent()}
      </TableLayout>
    );
  },
);

export { AddMoreCell, LoadMoreCell, CheckboxCell };

export default TableTree;
